import { useState, useEffect } from 'react'
import apiService from '../services/api'

const USER_TYPES = [
  { value: 'entrepreneur', label: 'Entrepreneur' },
  { value: 'individual', label: 'Individual' },
  { value: 'sme', label: 'SME' },
  { value: 'enterprise', label: 'Enterprise' }
]

const AdminUsers = () => {
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [updating, setUpdating] = useState(null)
  const [confirmDisable, setConfirmDisable] = useState(null)

  // Filter and sort state
  const [statusFilter, setStatusFilter] = useState('all')
  const [roleFilter, setRoleFilter] = useState('all')
  const [typeFilter, setTypeFilter] = useState('all')
  const [sortBy, setSortBy] = useState('created_desc')

  useEffect(() => {
    loadUsers()
  }, [])

  const loadUsers = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await apiService.listUsers()
      setUsers(data.users || [])
    } catch (err) {
      console.error('Error loading users:', err)
      setError(err.message || 'Failed to load users')
    } finally {
      setLoading(false)
    }
  }

  const handleToggleDisable = async (username, currentlyDisabled) => {
    try {
      setUpdating(username)
      setError(null)
      await apiService.disableUser(username, !currentlyDisabled)
      await loadUsers()
      setConfirmDisable(null)
    } catch (err) {
      console.error('Error updating user:', err)
      setError(err.message || 'Failed to update user')
    } finally {
      setUpdating(null)
    }
  }

  const handleActivateUser = async (username) => {
    try {
      setUpdating(username)
      setError(null)
      await apiService.activateUser(username)
      await loadUsers()
    } catch (err) {
      console.error('Error activating user:', err)
      setError(err.message || 'Failed to activate user')
    } finally {
      setUpdating(null)
    }
  }

  const isUserExpired = (user) => {
    if (!user.expires_at) return false
    const expiryTime = typeof user.expires_at === 'number'
      ? new Date(user.expires_at * 1000)
      : new Date(user.expires_at)
    return expiryTime < new Date()
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getStatusBadge = (user) => {
    if (user.disabled) {
      return (
        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-red-500/20 text-red-300 border border-red-500/30">
          Disabled
        </span>
      )
    }
    // Check if expired
    if (user.expires_at) {
      const expiryTime = typeof user.expires_at === 'number'
        ? new Date(user.expires_at * 1000)
        : new Date(user.expires_at)
      if (expiryTime < new Date()) {
        return (
          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-yellow-500/20 text-yellow-300 border border-yellow-500/30">
            Expired
          </span>
        )
      }
    }
    return (
      <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-500/20 text-green-300 border border-green-500/30">
        Active
      </span>
    )
  }

  const getRoleBadge = (role) => {
    if (role === 'admin') {
      return (
        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-purple-500/20 text-purple-300 border border-purple-500/30">
          Admin
        </span>
      )
    }
    return (
      <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-500/20 text-gray-300 border border-gray-500/30">
        User
      </span>
    )
  }

  const getInviteTypeBadge = (type) => {
    if (!type) return '-'
    const colors = {
      entrepreneur: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
      individual: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30',
      sme: 'bg-orange-500/20 text-orange-300 border-orange-500/30',
      enterprise: 'bg-pink-500/20 text-pink-300 border-pink-500/30'
    }
    const labels = {
      entrepreneur: 'Entrepreneur',
      individual: 'Individual',
      sme: 'SME',
      enterprise: 'Enterprise'
    }
    return (
      <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${colors[type] || 'bg-gray-500/20 text-gray-300 border-gray-500/30'}`}>
        {labels[type] || type}
      </span>
    )
  }

  // Get status string for filtering
  const getUserStatus = (user) => {
    if (user.disabled) return 'disabled'
    if (user.expires_at) {
      const expiryTime = typeof user.expires_at === 'number'
        ? new Date(user.expires_at * 1000)
        : new Date(user.expires_at)
      if (expiryTime < new Date()) return 'expired'
    }
    return 'active'
  }

  // Filter and sort users
  const filteredAndSortedUsers = users
    .filter(user => {
      if (statusFilter !== 'all' && getUserStatus(user) !== statusFilter) return false
      if (roleFilter !== 'all' && user.role !== roleFilter) return false
      if (typeFilter !== 'all' && user.invite_type !== typeFilter) return false
      return true
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'created_desc':
          return new Date(b.created_at || 0) - new Date(a.created_at || 0)
        case 'created_asc':
          return new Date(a.created_at || 0) - new Date(b.created_at || 0)
        case 'last_login_desc':
          return new Date(b.last_login || 0) - new Date(a.last_login || 0)
        case 'last_login_asc':
          return new Date(a.last_login || 0) - new Date(b.last_login || 0)
        case 'email_asc':
          return (a.email || '').localeCompare(b.email || '')
        case 'email_desc':
          return (b.email || '').localeCompare(a.email || '')
        case 'ideas_desc':
          return (b.idea_count || 0) - (a.idea_count || 0)
        case 'ideas_asc':
          return (a.idea_count || 0) - (b.idea_count || 0)
        case 'logins_desc':
          return (b.login_count || 0) - (a.login_count || 0)
        case 'logins_asc':
          return (a.login_count || 0) - (b.login_count || 0)
        default:
          return 0
      }
    })

  // Confirmation modal
  const ConfirmModal = ({ user, onClose, onConfirm }) => (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-2xl border border-gray-700 max-w-md w-full">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${user.disabled ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
              {user.disabled ? (
                <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              ) : (
                <svg className="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                </svg>
              )}
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">
                {user.disabled ? 'Enable User' : 'Disable User'}
              </h3>
              <p className="text-sm text-gray-400">{user.email}</p>
            </div>
          </div>
          <p className="text-gray-300 mb-6">
            {user.disabled
              ? 'This will re-enable the user\'s account, allowing them to log in and access the platform.'
              : 'This will prevent the user from logging in and accessing the platform. They can be re-enabled at any time.'}
          </p>
          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm font-medium bg-gray-700 text-white hover:bg-gray-600 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => onConfirm(user.username, user.disabled)}
              disabled={updating === user.username}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 flex items-center gap-2 ${
                user.disabled
                  ? 'bg-green-600 text-white hover:bg-green-500'
                  : 'bg-red-600 text-white hover:bg-red-500'
              }`}
            >
              {updating === user.username ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Processing...
                </>
              ) : (
                user.disabled ? 'Enable User' : 'Disable User'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )

  return (
    <>
      {error && (
        <div className="mb-6 bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-red-300">
          {error}
        </div>
      )}

      {/* Users Table */}
      <div className="bg-gray-800/50 rounded-2xl border border-gray-700/50 overflow-hidden">
        <div className="p-4 border-b border-gray-700/50">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">All Users</h3>
            <button
              onClick={loadUsers}
              className="px-3 py-1.5 rounded-lg text-sm font-medium bg-gray-700 text-gray-300 hover:bg-gray-600 transition-colors flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh
            </button>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-3 items-center">
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-400">Status:</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-2 py-1 rounded-lg text-sm bg-gray-700 border border-gray-600 text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="all">All</option>
                <option value="active">Active</option>
                <option value="expired">Expired</option>
                <option value="disabled">Disabled</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-400">Role:</label>
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="px-2 py-1 rounded-lg text-sm bg-gray-700 border border-gray-600 text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="all">All</option>
                <option value="admin">Admin</option>
                <option value="user">User</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-400">Type:</label>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="px-2 py-1 rounded-lg text-sm bg-gray-700 border border-gray-600 text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="all">All</option>
                {USER_TYPES.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-400">Sort:</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-2 py-1 rounded-lg text-sm bg-gray-700 border border-gray-600 text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="created_desc">Newest First</option>
                <option value="created_asc">Oldest First</option>
                <option value="last_login_desc">Recent Login</option>
                <option value="last_login_asc">Oldest Login</option>
                <option value="email_asc">Email A-Z</option>
                <option value="email_desc">Email Z-A</option>
                <option value="ideas_desc">Most Ideas</option>
                <option value="ideas_asc">Fewest Ideas</option>
                <option value="logins_desc">Most Logins</option>
                <option value="logins_asc">Fewest Logins</option>
              </select>
            </div>
            <span className="text-xs text-gray-500 ml-auto">
              Showing {filteredAndSortedUsers.length} of {users.length}
            </span>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
          </div>
        ) : filteredAndSortedUsers.length === 0 ? (
          <div className="text-center py-16">
            <svg className="w-16 h-16 text-gray-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
            <h3 className="text-lg font-medium text-gray-300 mb-2">
              {users.length === 0 ? 'No users yet' : 'No matching users'}
            </h3>
            <p className="text-gray-500">
              {users.length === 0 ? 'Users will appear here once they register' : 'Try adjusting your filters'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-900/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">User</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Role</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Ideas</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Logins</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Created</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Last Login</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700/50">
                {filteredAndSortedUsers.map((user) => (
                  <tr key={user.username} className="hover:bg-gray-700/30">
                    <td className="px-4 py-3">
                      <div className="text-sm text-white">{user.email}</div>
                      <div className="text-xs text-gray-500 font-mono">{user.username}</div>
                    </td>
                    <td className="px-4 py-3">{getRoleBadge(user.role)}</td>
                    <td className="px-4 py-3">{getInviteTypeBadge(user.invite_type)}</td>
                    <td className="px-4 py-3 text-sm text-gray-300">{user.idea_count || 0}</td>
                    <td className="px-4 py-3 text-sm text-gray-300">{user.login_count || 0}</td>
                    <td className="px-4 py-3">{getStatusBadge(user)}</td>
                    <td className="px-4 py-3 text-sm text-gray-400">{formatDate(user.created_at)}</td>
                    <td className="px-4 py-3 text-sm text-gray-400">{formatDate(user.last_login)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {isUserExpired(user) && !user.disabled && (
                          <button
                            onClick={() => handleActivateUser(user.username)}
                            disabled={updating === user.username}
                            className="px-3 py-1 rounded-lg text-xs font-medium transition-colors flex items-center gap-1 bg-blue-600/20 text-blue-300 hover:bg-blue-600/30"
                          >
                            {updating === user.username ? (
                              <div className="w-3 h-3 border-2 border-blue-300/30 border-t-blue-300 rounded-full animate-spin" />
                            ) : (
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            )}
                            Activate
                          </button>
                        )}
                        <button
                          onClick={() => setConfirmDisable(user)}
                          disabled={updating === user.username}
                          className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors flex items-center gap-1 ${
                            user.disabled
                              ? 'bg-green-600/20 text-green-300 hover:bg-green-600/30'
                              : 'bg-red-600/20 text-red-300 hover:bg-red-600/30'
                          }`}
                        >
                          {user.disabled ? (
                            <>
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              Enable
                            </>
                          ) : (
                            <>
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                              </svg>
                              Disable
                            </>
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      {confirmDisable && (
        <ConfirmModal
          user={confirmDisable}
          onClose={() => setConfirmDisable(null)}
          onConfirm={handleToggleDisable}
        />
      )}
    </>
  )
}

export default AdminUsers
